/**
 * The published policies.
 *
 * Kept as data rather than JSX so the four documents share one layout and stay
 * mechanically consistent — a policy that contradicts another policy is worse
 * than one that is merely plain.
 *
 * These describe what the software actually does. Where a clause depends on a
 * fact only the operator knows — the legal entity, the registered address — it
 * is marked and must be filled in before launch. They have not been reviewed by
 * counsel; see docs/STRIPE-APPLICATION.md.
 */

export const OPERATOR = {
  /** TODO: replace with the incorporated entity before taking real money. */
  legalName: 'ClickLinx (operating entity to be confirmed)',
  address: 'Ontario, Canada',
  support: 'support@clicklinx.ca',
  privacy: 'privacy@clicklinx.ca',
}

export const EFFECTIVE_DATE = '29 July 2026'

export const BLOCKED_US_STATES =
  'Arizona, Arkansas, Connecticut, Delaware, Idaho, Louisiana, Montana, Nevada, ' +
  'South Carolina, South Dakota, Tennessee and Washington'

export interface Section {
  heading: string
  paragraphs?: string[]
  list?: string[]
}

export interface LegalDocument {
  slug: 'terms' | 'privacy' | 'refunds' | 'rules'
  path: string
  title: string
  /** Shown under the title, and as the meta description. */
  summary: string
  sections: Section[]
}

const terms: LegalDocument = {
  slug: 'terms',
  path: '/terms',
  title: 'Terms of Service',
  summary:
    'The agreement between you and ClickLinx covering accounts, entry fees, prizes and conduct.',
  sections: [
    {
      heading: '1. What ClickLinx is',
      paragraphs: [
        `ClickLinx is a platform for real-time contests of skill. Players enter timed reaction and counting contests against other players. Where a contest has an entry fee, every entrant pays the same amount into a prize pool and the winner receives that pool.`,
        `ClickLinx is not a casino and does not offer games of chance. Contest outcomes are determined by the speed and accuracy of the entrants. ClickLinx does not participate in any prize contest and has no stake in who wins.`,
        `These terms form an agreement between you and ${OPERATOR.legalName} ("we", "us"). By creating an account you accept them.`,
      ],
    },
    {
      heading: '2. Eligibility',
      paragraphs: [
        `To enter a prize contest you must be an individual, acting for yourself, who meets the minimum age where you are located, and be located somewhere we offer prize contests.`,
        `The minimum age is 18 in most places, 19 in Alabama, Nebraska, and in British Columbia, New Brunswick, Newfoundland and Labrador, Northwest Territories, Nova Scotia, Nunavut, Ontario, Saskatchewan and Yukon, and 21 in Massachusetts. We ask for your date of birth and check it against the requirement that applies where you are playing from, which means the answer can change if you travel.`,
        `Prize contests are offered in the United States and Canada only, and not in ${BLOCKED_US_STATES}, nor in Quebec. If we cannot determine where you are, we treat prize contests as unavailable rather than assume otherwise.`,
        `Practice contests carry no entry fee and no prize, and are open everywhere without an age check.`,
      ],
    },
    {
      heading: '3. Your account',
      paragraphs: [
        `One account per person. Keep your credentials to yourself; you are responsible for activity under your account.`,
        `Give us accurate information. Entering a false date of birth to reach a contest you are not eligible for is a breach of these terms and grounds for closing the account.`,
        `You may close your account at any time. We may suspend or close an account that breaches these terms, and will tell you why.`,
      ],
    },
    {
      heading: '4. Practice coins and prize coins',
      paragraphs: [
        `Practice coins are free. We grant them at signup and top them up daily. They have no monetary value, cannot be bought, sold, transferred or redeemed, and are used only in practice contests.`,
        `Prize coins represent money you have added to your account. They convert to and from your withdrawable balance at a fixed rate of 100 prize coins to one dollar, in both directions, with no fee and no rounding.`,
        `The two are separate and never mix. No practice coin ever becomes money.`,
      ],
    },
    {
      heading: '5. Entry fees and prizes',
      paragraphs: [
        `Entering a prize contest debits the entry fee from your prize coin balance when the contest begins. The prize pool is the total of all entry fees, and is paid to the winner in full. Where entrants tie, the pool is divided evenly and any indivisible remainder is allocated in seat order.`,
        `A contest that cannot be completed — for example if every entrant disconnects — is void, and every entry fee is returned.`,
        `We take no share of any prize pool. Our fee is charged on withdrawal, as set out in the Refund and Payout Policy.`,
      ],
    },
    {
      heading: '6. Fair play',
      paragraphs: [
        `Contests are decided on the server. Your device reports what you did; it never reports the result.`,
        `You must not use scripts, macros, automation, or any tool that generates input on your behalf, and you must not interfere with the service or with other players. We analyse input timing for signatures of automation.`,
        `Where we find automated input, the contest is void, the entry fee is not returned, and the finding is recorded against the account. Repeated findings lead to closure.`,
      ],
    },
    {
      heading: '7. Withdrawals',
      paragraphs: [
        `You may withdraw your balance subject to the minimum, the fee and the process described in the Refund and Payout Policy.`,
        `We may hold a withdrawal while we investigate a suspected breach of these terms. If the investigation clears you, the withdrawal proceeds.`,
      ],
    },
    {
      heading: '8. Availability',
      paragraphs: [
        `We aim to keep the service running but do not promise uninterrupted availability. We may change or withdraw contest formats, and we will not do so in a way that takes an entry fee for a contest you cannot then play.`,
        `If we close the service, we will give notice and allow you to withdraw your remaining balance.`,
      ],
    },
    {
      heading: '9. Liability',
      paragraphs: [
        `Nothing in these terms limits liability for fraud, for death or personal injury caused by negligence, or for anything else that cannot be limited by law.`,
        `Subject to that, our total liability to you is limited to the amount you have added to your account in the twelve months before the claim.`,
        `We are not liable for losses caused by your equipment or connection, including a contest lost to lag or a device failure.`,
      ],
    },
    {
      heading: '10. Changes and governing law',
      paragraphs: [
        `We may change these terms. Material changes will be notified in the app before they take effect, and continuing to use the service after that is acceptance.`,
        `These terms are governed by the laws of Ontario, Canada, and the courts of Ontario have jurisdiction, without depriving you of any protection under the law where you live.`,
        `Questions: ${OPERATOR.support}`,
      ],
    },
  ],
}

const privacy: LegalDocument = {
  slug: 'privacy',
  path: '/privacy',
  title: 'Privacy Policy',
  summary: 'What we collect, why we collect it, who else sees it, and how long we keep it.',
  sections: [
    {
      heading: 'What we collect',
      list: [
        'Email address and display name — to create and identify your account.',
        'Password — held by Amazon Cognito, our authentication provider. We never see or store it.',
        'Date of birth — to check you meet the minimum age where you are playing. Stored as a date rather than a yes/no, because the requirement differs by region and has to be re-checked if you move.',
        'IP address — to determine which country and region you are in, so we can apply the right age threshold and decline prize contests where they are not offered. Also recorded against each age attestation.',
        'Gameplay records — contests entered, scores, timing of your input, and the outcome. Input timing is what lets us tell human play from automation.',
        'Financial records — the amount and time of each purchase, conversion, contest entry, prize and withdrawal. Card details are handled by Stripe and never reach our servers. Your PayPal address is stored so we can pay you.',
      ],
    },
    {
      heading: 'Why we are allowed to hold it',
      paragraphs: [
        `We process your account details and gameplay records because they are necessary to provide the service you asked for.`,
        `We process date of birth and location because we have a legal obligation to restrict paid contests by age and jurisdiction, and a legitimate interest in not offering a product where it is not lawful.`,
        `We process input timing and keep financial records because we have a legitimate interest in detecting cheating and in maintaining an auditable record of money movements.`,
      ],
    },
    {
      heading: 'Who else sees it',
      list: [
        'Amazon Web Services — hosting and database, in the US East region.',
        'Amazon Cognito — accounts and passwords.',
        'Stripe — card payments. Stripe receives your email and the amount; we receive confirmation and never the card.',
        'PayPal — receives the email address you nominate and the payout amount.',
        'Google Analytics — anonymised usage measurement. It does not receive your email, date of birth or balance.',
        'IP geolocation providers — receive your IP address only, to return a country and region.',
      ],
      paragraphs: [
        `We do not sell your personal information, and we do not share it for advertising.`,
        `We will disclose information where we are legally required to, and will tell you unless we are prohibited from doing so.`,
      ],
    },
    {
      heading: 'How long we keep it',
      paragraphs: [
        `Account and gameplay records are kept while your account is open. If you close it, we delete your account details but retain financial records and the ledger entries behind them for seven years, because we are required to keep records of money movements and because deleting half of a double-entry ledger would corrupt what remains.`,
        `Age attestations are kept for as long as the account exists plus seven years, as evidence that the check was carried out.`,
      ],
    },
    {
      heading: 'Your rights',
      paragraphs: [
        `You can ask for a copy of what we hold, ask us to correct it, ask us to delete it, or object to how we use it. Write to ${OPERATOR.privacy} and we will respond within thirty days.`,
        `Deletion is subject to the retention above: we can remove your identity from our records but cannot remove a financial transaction that we are obliged to keep.`,
        `If you are in Canada you may complain to the Office of the Privacy Commissioner. If you are in the EU or UK you may complain to your local supervisory authority.`,
      ],
    },
    {
      heading: 'Cookies and storage',
      paragraphs: [
        `We store your session token in your browser so you stay signed in. This is necessary for the service and cannot be turned off while you are signed in.`,
        `Google Analytics sets cookies to measure usage. You can block these without affecting gameplay.`,
      ],
    },
    {
      heading: 'Children',
      paragraphs: [
        `ClickLinx is not for children. We do not knowingly collect information from anyone under 13, and paid contests require an age above that in every region we serve. If you believe a child has an account, write to ${OPERATOR.privacy} and we will remove it.`,
      ],
    },
  ],
}

const refunds: LegalDocument = {
  slug: 'refunds',
  path: '/refunds',
  title: 'Refund and Payout Policy',
  summary: 'When money can come back out, what it costs, and how long it takes.',
  sections: [
    {
      heading: 'Adding funds',
      paragraphs: [
        `Money added to your account is credited when Stripe confirms the payment. It sits as a withdrawable balance until you convert it into prize coins to enter contests.`,
        `Adding funds is not itself a purchase of anything consumable. Until you enter a contest, the full amount remains withdrawable.`,
      ],
    },
    {
      heading: 'Entry fees are not refundable once a contest starts',
      paragraphs: [
        `When a contest begins, the entry fee leaves your balance and forms part of the prize pool. It cannot be returned, because it has been paid to whoever wins.`,
        `This is the single most important thing on this page, and it is shown to you before you enter.`,
      ],
      list: [
        'A contest that cannot be completed is void and every entry fee is returned in full.',
        'An entry fee is not returned where the contest is void because you used automated input.',
        'Losing is not grounds for a refund.',
      ],
    },
    {
      heading: 'Withdrawing',
      list: [
        'Minimum balance to withdraw: $10.00.',
        'You may withdraw any amount up to your available balance once you meet the minimum.',
        'Fee: 15% of the amount withdrawn. This is our only revenue.',
        'Paid to the PayPal address you give us, within 24 to 48 hours.',
        'One open request at a time.',
      ],
      paragraphs: [
        `The amount you request is held out of your spendable balance the moment you ask, so the same funds cannot be entered into a contest while the payout is queued. You can cancel a request before it is paid and the hold is released.`,
        `Prize coins must be converted back to your withdrawable balance before they can be paid out. Conversion is free, instant, and at the same rate in both directions.`,
      ],
    },
    {
      heading: 'Unused balance',
      paragraphs: [
        `Money you added and never entered into a contest can be withdrawn under the terms above. We do not expire balances and we do not charge inactivity fees.`,
        `If you believe you were charged in error, write to ${OPERATOR.support} within 60 days and we will investigate.`,
      ],
    },
    {
      heading: 'Chargebacks',
      paragraphs: [
        `If you dispute a payment with your card issuer rather than contacting us, we will suspend the account while the dispute is open, because we cannot pay out funds that may be reversed.`,
        `Talk to us first. Most disputes are a misunderstanding about the point above — that an entry fee has already been paid to another player — and we would rather explain it than have your account frozen.`,
      ],
    },
  ],
}

const rules: LegalDocument = {
  slug: 'rules',
  path: '/rules',
  title: 'Contest Rules',
  summary: 'The three formats, how they are scored, and how ties are settled.',
  sections: [
    {
      heading: 'Common to every contest',
      paragraphs: [
        `Two to four players enter. Everyone pays the same entry fee and everyone sees the same contest.`,
        `Contest content is generated from a single seed that is recorded against the contest. Every entrant receives the same shapes, in the same positions, at the same times, measured from a shared start signal. No randomness distinguishes one entrant's contest from another's, and any contest can be regenerated from its seed for review.`,
        `Scoring happens on our server. Your device reports what you did — clicks and answers — and never reports whether you were right.`,
        `Network round-trip time is measured continuously and subtracted from your submissions, so a slower connection does not by itself cost you a contest.`,
      ],
    },
    {
      heading: 'Click Race',
      paragraphs: [
        `A single round of 30, 60 or 90 seconds, chosen by the host. Click as many times as you can before the clock runs out. The highest count wins.`,
        `Clicks are reported as offsets from the start of the round and validated for automation before they count.`,
      ],
    },
    {
      heading: 'Sequential Count',
      paragraphs: [
        `One shape appears and disappears repeatedly. Count how many times it appears. When the display window closes, everything vanishes and you have five seconds to enter your number.`,
        `The first round's display window is 10, 20 or 30 seconds, chosen by the host. Every round after that is faster and contains more.`,
      ],
    },
    {
      heading: 'Scatter Count',
      paragraphs: [
        `You are told which shape to count. The screen then fills with that shape among others, and is replaced wholesale every few seconds. Your answer is the total across every screen, not the last one.`,
        `As with Sequential Count, the host chooses the first round's window and each subsequent round tightens it.`,
      ],
    },
    {
      heading: 'Elimination and ties',
      list: [
        'In the counting formats, an entrant who answers correctly while another answers incorrectly knocks that entrant out.',
        'If everyone answers the same way — all correct or all incorrect — nobody goes out and the round runs again, harder.',
        'Play continues until one entrant remains, or until 30 rounds have been played.',
        'Where entrants are still level at the end, the faster correct answer wins.',
        'Where they remain level after that, the prize pool is divided evenly between them.',
      ],
    },
    {
      heading: 'Practice contests',
      paragraphs: [
        `Practice contests use practice coins and can be played against other people or against our bot. When you play the bot, we put up its side of the pool, so beating it wins you practice coins and losing to it costs you the coins you entered.`,
        `The bot never appears in a prize contest. Prize contests are between people only.`,
      ],
    },
    {
      heading: 'Automation',
      paragraphs: [
        `Input is checked for rate, minimum spacing and variance. Human input is irregular; generated input is not, and the difference is measurable.`,
        `A contest found to involve automated input is void. The entry fee is not returned and the finding is recorded against the account.`,
      ],
    },
  ],
}

export const LEGAL_DOCUMENTS: LegalDocument[] = [terms, privacy, refunds, rules]

export function documentForPath(pathname: string): LegalDocument | null {
  const clean = pathname.replace(/\/+$/, '').toLowerCase() || '/'
  return LEGAL_DOCUMENTS.find((doc) => doc.path === clean) ?? null
}
